import { useEffect, useRef, useState } from "react";
import "./App.css";
import { quotes } from "./quotes";

type QuoteDeck = {
  currentIndex: number;
  upcomingIndices: number[];
};

type StoredQuoteDeck = QuoteDeck & {
  quoteSignature: string;
};

const QUOTE_DECK_STORAGE_KEY = "rew-quotes-deck-v1";
const QUOTE_SIGNATURE = quotes.join("||");
const QUOTE_TRANSITION_MS = 180;

const shuffleIndices = (length: number, previousIndex?: number) => {
  const indices = Array.from({ length }, (_, index) => index);

  for (let index = indices.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [indices[index], indices[randomIndex]] = [
      indices[randomIndex],
      indices[index],
    ];
  }

  if (indices.length > 1 && indices[0] === previousIndex) {
    const swapIndex = Math.floor(Math.random() * (indices.length - 1)) + 1;
    [indices[0], indices[swapIndex]] = [indices[swapIndex], indices[0]];
  }

  return indices;
};

const createQuoteDeck = (previousIndex?: number): QuoteDeck => {
  const shuffledQuotes = shuffleIndices(quotes.length, previousIndex);

  return {
    currentIndex: shuffledQuotes[0] ?? 0,
    upcomingIndices: shuffledQuotes.slice(1),
  };
};

const isValidQuoteDeck = (deck: unknown): deck is QuoteDeck => {
  if (!deck || typeof deck !== "object") {
    return false;
  }

  const maybeDeck = deck as Partial<StoredQuoteDeck>;

  if (
    maybeDeck.quoteSignature !== QUOTE_SIGNATURE ||
    typeof maybeDeck.currentIndex !== "number" ||
    !Array.isArray(maybeDeck.upcomingIndices)
  ) {
    return false;
  }

  const allIndices = [maybeDeck.currentIndex, ...maybeDeck.upcomingIndices];
  const uniqueIndices = new Set(allIndices);

  return (
    uniqueIndices.size === allIndices.length &&
    allIndices.every(
      (index) => Number.isInteger(index) && index >= 0 && index < quotes.length,
    )
  );
};

const getInitialQuoteDeck = () => {
  try {
    const storedDeck = window.localStorage.getItem(QUOTE_DECK_STORAGE_KEY);

    if (storedDeck) {
      const parsedDeck = JSON.parse(storedDeck);

      if (isValidQuoteDeck(parsedDeck)) {
        return {
          currentIndex: parsedDeck.currentIndex,
          upcomingIndices: parsedDeck.upcomingIndices,
        };
      }
    }
  } catch {
    // Ignore storage errors and start a fresh deck.
  }

  return createQuoteDeck();
};

function App() {
  const [quoteDeck, setQuoteDeck] = useState(getInitialQuoteDeck);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimerRef = useRef<number | null>(null);

  const currentQuote = quotes[quoteDeck.currentIndex];
  const remainingQuotes = quoteDeck.upcomingIndices.length;
  const currentQuoteNumber = quotes.length - remainingQuotes;
  const quoteSizeClass =
    currentQuote.length >= 76
      ? "quote--long"
      : currentQuote.length >= 48
        ? "quote--medium"
        : "";

  useEffect(() => {
    try {
      window.localStorage.setItem(
        QUOTE_DECK_STORAGE_KEY,
        JSON.stringify({
          ...quoteDeck,
          quoteSignature: QUOTE_SIGNATURE,
        }),
      );
    } catch {
      // Local storage is a convenience, so the app can still run without it.
    }
  }, [quoteDeck]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const getNextQuote = () => {
    if (isTransitioning) {
      return;
    }

    setIsTransitioning(true);
    transitionTimerRef.current = window.setTimeout(() => {
      setQuoteDeck(({ currentIndex, upcomingIndices }) => {
        const nextDeck =
          upcomingIndices.length > 0
            ? upcomingIndices
            : shuffleIndices(quotes.length, currentIndex);
        const [nextIndex = currentIndex, ...remainingIndices] = nextDeck;

        return {
          currentIndex: nextIndex,
          upcomingIndices: remainingIndices,
        };
      });

      setIsTransitioning(false);
      transitionTimerRef.current = null;
    }, QUOTE_TRANSITION_MS);
  };

  return (
    <div className="outer-container">
      <main className="app-container">
        <header className="title-container">
          <p className="eyebrow">Quote Generator</p>
          <h1 className="title">Andrew, CEO</h1>
        </header>

        <div className="quote-card">
          <span className="quote-mark" aria-hidden="true">
            &ldquo;
          </span>
          <div
            className={`quote-content ${isTransitioning ? "quote-content--exit" : ""}`}
            key={quoteDeck.currentIndex}
          >
            <h2 className={`quote ${quoteSizeClass}`}>{currentQuote}</h2>
          </div>
          <div className="quote-controls">
            <p className="quote-progress">
              <span>
                Quote {currentQuoteNumber} of {quotes.length}
              </span>
              <span>{remainingQuotes} unseen remaining</span>
            </p>
            <button
              className="refresh-button"
              disabled={isTransitioning}
              onClick={getNextQuote}
            >
              Random Quote
            </button>
          </div>
        </div>

        <footer className="brand-footer">REW Quotes</footer>
      </main>
    </div>
  );
}

export default App;
